import React, { useEffect, useState } from 'react';
import { Plus, Search, DollarSign, Truck as TruckIcon, Send, Edit, Trash2, FileText } from 'lucide-react';
import { LoadDetailView } from './LoadDetailView';
import { supabase } from '../../lib/supabase';
import { fetchAllRows } from '../../lib/fetchAllRows';
import { DispatchModal } from './DispatchModal';
import { useToast } from '../../contexts/ToastContext';
import { useNavigate } from '../../hooks/useNavigate';
import { useAuth } from '../../contexts/AuthContext';
import { ConfirmModal } from '../common/ConfirmModal';
import { AIRateConfUpload } from '../documents/AIRateConfUpload';

interface Load {
  id: string;
  load_number: string;
  status: string;
  commodity: string;
  weight: number;
  total_rate: number;
  total_miles: number;
  pickup_date: string;
  delivery_date: string;
  rescheduled_delivery_date?: string;
  rescheduled_delivery_time_from?: string;
  rescheduled_delivery_time_to?: string;
  pickup_city?: string;
  pickup_state?: string;
  delivery_city?: string;
  delivery_state?: string;
  customers?: { name: string };
  drivers?: { first_name: string; last_name: string };
  trucks?: { unit_number: string };
  driver_id?: string;
  truck_id?: string;
  trailer_id?: string;
  created_at?: string;
  created_by?: string;
  created_by_user?: string;
}

const LOADS_VIEW_STATE_KEY = 'loadsView:filterState';

type LoadsViewPersistedState = {
  searchTerm: string;
  statusFilter: string;
  driverFilter: string;
  truckFilter: string;
  createdByFilter: string;
  currentPage: number;
};

function readPersistedLoadsViewState(): Partial<LoadsViewPersistedState> {
  try {
    const raw = sessionStorage.getItem(LOADS_VIEW_STATE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

export function LoadsView() {
  const navigate = useNavigate();
  const toast = useToast();
  const { profile } = useAuth();
  const persisted = readPersistedLoadsViewState();
  const [allLoads, setAllLoads] = useState<Load[]>([]); // Store all loads for counts
  const [loads, setLoads] = useState<Load[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState(persisted.searchTerm ?? '');
  const [statusFilter, setStatusFilter] = useState(persisted.statusFilter ?? 'all');
  const [driverFilter, setDriverFilter] = useState(persisted.driverFilter ?? 'all');
  const [truckFilter, setTruckFilter] = useState(persisted.truckFilter ?? 'all');
  const [createdByFilter, setCreatedByFilter] = useState(persisted.createdByFilter ?? 'all');
  const [dispatchLoad, setDispatchLoad] = useState<Load | null>(null);
  const [selectedLoadNumber, setSelectedLoadNumber] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [loadToDelete, setLoadToDelete] = useState<Load | null>(null);
  const [showAIUpload, setShowAIUpload] = useState(false);
  const [selectedLoads, setSelectedLoads] = useState<Set<string>>(new Set());
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [currentPage, setCurrentPage] = useState(persisted.currentPage ?? 1);
  const itemsPerPage = 20;

  useEffect(() => {
    try {
      sessionStorage.setItem(LOADS_VIEW_STATE_KEY, JSON.stringify({
        searchTerm,
        statusFilter,
        driverFilter,
        truckFilter,
        createdByFilter,
        currentPage,
      }));
    } catch {
      // ignore quota / privacy-mode errors
    }
  }, [searchTerm, statusFilter, driverFilter, truckFilter, createdByFilter, currentPage]);
  
  // Check if user can access AI Rate Confirmation (Dispatch, Dispatch Manager, Admin, or Superadmin)
  const canUseAIRateConf = profile?.role === 'dispatch' || profile?.role === 'Dispatch' || profile?.role === 'Dispatch Manager' || profile?.role === 'admin' || profile?.role === 'Admin' || profile?.role === 'superadmin' || profile?.role === 'Superadmin';

  useEffect(() => {
    loadLoads();
    
    // Subscribe to dispatch changes to refresh loads when dispatches are deleted
    const dispatchSubscription = supabase
      .channel('dispatch-changes')
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: 'dispatches' },
        () => {
          // Reload loads when any dispatch is created, updated, or deleted
          loadLoads();
        }
      )
      .subscribe();

    // Subscribe to load changes to refresh when loads are updated
    const loadSubscription = supabase
      .channel('load-changes')
      .on('postgres_changes', 
        { event: 'UPDATE', schema: 'public', table: 'loads' },
        () => {
          // Reload loads when any load is updated
          loadLoads();
        }
      )
      .subscribe();

    return () => {
      dispatchSubscription.unsubscribe();
      loadSubscription.unsubscribe();
    };
  }, []); // Load once on mount

  // Filter loads when statusFilter changes
  useEffect(() => {
    filterLoads();
  }, [statusFilter, allLoads]);

  // Filter loads based on status filter
  const filterLoads = (loadsToFilter = allLoads) => {
    let filtered = loadsToFilter;

    if (statusFilter !== 'all') {
      if (statusFilter === 'dispatched') {
        // Include both dispatched and in_transit
        filtered = loadsToFilter.filter(l => l.status?.toUpperCase() === 'DISPATCHED' || l.status?.toUpperCase() === 'IN_TRANSIT');
      } else if (statusFilter === 'booked') {
        filtered = loadsToFilter.filter(l => l.status?.toUpperCase() === 'BOOKED');
      } else if (statusFilter === 'delivered') {
        filtered = loadsToFilter.filter(l => l.status?.toUpperCase() === 'DELIVERED');
      } else if (statusFilter === 'archived') {
        filtered = loadsToFilter.filter(l => l.status?.toUpperCase() === 'ARCHIVED');
      } else {
        filtered = loadsToFilter.filter(l => l.status?.toUpperCase() === statusFilter.toUpperCase());
      }
    }

    setLoads(filtered);
  };

  const handleDeleteLoad = async () => {
    if (!loadToDelete) return;

    const isTonuDelivered = loadToDelete.status === 'delivered' && (loadToDelete as any).is_tonu;

    // Hard delete path:
    //  - Superadmin removing an already-soft-removed load.
    //  - Anyone removing a TONU that was auto-delivered. TONU loads have no real
    //    trip/documents tied to them so there's nothing to archive.
    if ((loadToDelete.status === 'removed' && profile?.role === 'Superadmin') || isTonuDelivered) {
      try {
        // Detach documents (the link lives in load_documents, not documents.load_id).
        await supabase
          .from('load_documents')
          .delete()
          .eq('load_id', loadToDelete.id);

        // TONU loads also have a dispatch row; clear it so the FK doesn't block the load delete.
        if (isTonuDelivered) {
          await supabase
            .from('dispatches')
            .delete()
            .eq('load_id', loadToDelete.id);
        }

        // Delete the load permanently
        const { error } = await supabase
          .from('loads')
          .delete()
          .eq('id', loadToDelete.id);

        if (error) throw error;

        toast.success(isTonuDelivered ? 'TONU load deleted.' : 'Load deleted permanently!');
        setShowDeleteConfirm(false);
        setLoadToDelete(null);
        loadLoads();
      } catch (error: any) {
        console.error('Error deleting load:', error);
        toast.error('Failed to delete load permanently');

        setShowDeleteConfirm(false);
        setLoadToDelete(null);
      }
      return;
    }

    // Prevent removing non-TONU delivered loads — they should be archived (except for Superadmins).
    if (loadToDelete.status === 'delivered' && profile?.role !== 'Superadmin') {
      toast.error('Cannot remove delivered loads. Please send to accounting to archive.');
      setShowDeleteConfirm(false);
      setLoadToDelete(null);
      return;
    }

    try {
      // Rename load number to include _REMOVED suffix and mark as removed
      const newLoadNumber = `${loadToDelete.load_number}_REMOVED_${Date.now()}`;
      
      const { error } = await supabase
        .from('loads')
        .update({ 
          status: 'removed',
          load_number: newLoadNumber
        })
        .eq('id', loadToDelete.id);

      if (error) throw error;

      toast.success('Load marked as removed successfully!');
      setShowDeleteConfirm(false);
      setLoadToDelete(null);
      loadLoads();
    } catch (error: any) {
      console.error('Error removing load:', error);
      toast.error('Failed to remove load');
      
      setShowDeleteConfirm(false);
      setLoadToDelete(null);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedLoads.size === 0) return;

    try {
      const loadsToDelete = filteredLoads.filter(load => selectedLoads.has(load.id));
      
      // Check if any are delivered non-TONU (those still require Superadmin / archive flow)
      const deliveredNonTonu = loadsToDelete.filter(
        l => l.status === 'delivered' && !(l as any).is_tonu
      );
      if (deliveredNonTonu.length > 0 && profile?.role !== 'Superadmin') {
        toast.error(`Cannot remove ${deliveredNonTonu.length} delivered load(s). Please send to accounting to archive.`);
        return;
      }

      // TONU delivered loads get hard-deleted (no real trip/docs to archive).
      const tonuDelivered = loadsToDelete.filter(
        l => l.status === 'delivered' && (l as any).is_tonu
      );
      if (tonuDelivered.length > 0) {
        const ids = tonuDelivered.map(l => l.id);
        await supabase.from('load_documents').delete().in('load_id', ids);
        await supabase.from('dispatches').delete().in('load_id', ids);
        const { error } = await supabase.from('loads').delete().in('id', ids);
        if (error) throw error;
      }

      // Separate removed loads (permanent delete for superadmins) from others (mark as removed)
      const removedLoads = loadsToDelete.filter(l => l.status === 'removed');
      const activeLoads = loadsToDelete.filter(l =>
        l.status !== 'removed' &&
        !(l.status === 'delivered' && (l as any).is_tonu)
      );

      // Permanent delete for removed loads (Superadmin only)
      if (removedLoads.length > 0 && profile?.role === 'Superadmin') {
        const { error } = await supabase
          .from('loads')
          .delete()
          .in('id', removedLoads.map(l => l.id));

        if (error) throw error;
      }

      // Mark active loads as removed
      if (activeLoads.length > 0) {
        const updates = activeLoads.map(load => ({
          id: load.id,
          status: 'removed',
          load_number: `${load.load_number}_REMOVED_${Date.now()}`
        }));

        for (const update of updates) {
          const { error } = await supabase
            .from('loads')
            .update({ status: update.status, load_number: update.load_number })
            .eq('id', update.id);

          if (error) throw error;
        }
      }

      toast.success(`${selectedLoads.size} load(s) deleted successfully!`);
      setSelectedLoads(new Set());
      setShowBulkDeleteConfirm(false);
      loadLoads();
    } catch (error: any) {
      console.error('Error bulk deleting loads:', error);
      toast.error('Failed to delete loads');
      setShowBulkDeleteConfirm(false);
    }
  };

  const toggleSelectLoad = (loadId: string) => {
    const newSelected = new Set(selectedLoads);
    if (newSelected.has(loadId)) {
      newSelected.delete(loadId);
    } else {
      newSelected.add(loadId);
    }
    setSelectedLoads(newSelected);
  };

  const toggleSelectAll = () => {
    if (selectedLoads.size === filteredLoads.length) {
      setSelectedLoads(new Set());
    } else {
      setSelectedLoads(new Set(filteredLoads.map(l => l.id)));
    }
  };

  async function loadLoads() {
    try {
      const data = await fetchAllRows(() => {
        let query = supabase
          .from('loads')
          .select(`
            *,
            customers(name),
            drivers(first_name, last_name),
            trucks(unit_number),
            created_by_profile:profiles!loads_created_by_fkey(full_name),
            load_stops!load_stops_load_id_fkey(
              stop_type,
              stop_order,
              date,
              time_from,
              city,
              state
            )
          `);

        // Filter loads based on role
        // Dispatch role: only see their own loads
        // Dispatch Manager, Admin, Superadmin: see all loads
        if (profile?.role === 'Dispatch') {
          query = query.eq('created_by', profile.id);
        }

        return query;
      });

      // Process loads to populate pickup/delivery dates from load_stops if available
      const processedData = (data || []).map((load: any) => {
        if (load.load_stops && load.load_stops.length > 0) {
          // Sort stops by stop_order
          const sortedStops = [...load.load_stops].sort((a, b) => a.stop_order - b.stop_order);
          
          // Find first pickup stop
          const pickupStop = sortedStops.find(s => s.stop_type === 'shipper');
          if (pickupStop && pickupStop.date) {
            const pickupDateTime = pickupStop.time_from 
              ? `${pickupStop.date}T${pickupStop.time_from}`
              : pickupStop.date;
            load.pickup_date = pickupDateTime;
            load.pickup_city = pickupStop.city;
            load.pickup_state = pickupStop.state;
          }
          
          // Find last delivery stop
          const deliveryStops = sortedStops.filter(s => s.stop_type === 'consignee');
          if (deliveryStops.length > 0) {
            const lastDelivery = deliveryStops[deliveryStops.length - 1];
            if (lastDelivery.date) {
              const deliveryDateTime = lastDelivery.time_from 
                ? `${lastDelivery.date}T${lastDelivery.time_from}`
                : lastDelivery.date;
              load.delivery_date = deliveryDateTime;
              load.delivery_city = lastDelivery.city;
              load.delivery_state = lastDelivery.state;
            }
          }
        }
        return load;
      });
      
      // Group loads by user (created_by), then sort by created_at within each group
      const sortedData = processedData.sort((a, b) => {
        // First, sort by created_by (user)
        const userA = (a as any).created_by_profile?.full_name || 'System';
        const userB = (b as any).created_by_profile?.full_name || 'System';
        
        if (userA !== userB) {
          return userA.localeCompare(userB);
        }
        
        // Within same user, sort by created_at (newest first)
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      });
      
      // Filter out removed loads (those with _REMOVED_ in load number)
      const nonRemovedLoads = sortedData.filter(load => !load.load_number?.includes('_REMOVED_'));
      
      console.log('Setting allLoads with', nonRemovedLoads.length, 'loads');
      console.log('Unique status values:', [...new Set(nonRemovedLoads.map(l => l.status))]);
      console.log('Status breakdown:', {
        total: nonRemovedLoads.length,
        booked: nonRemovedLoads.filter(l => l.status?.toUpperCase() === 'BOOKED').length,
        dispatched: nonRemovedLoads.filter(l => l.status?.toUpperCase() === 'DISPATCHED' || l.status?.toUpperCase() === 'IN_TRANSIT').length,
        delivered: nonRemovedLoads.filter(l => l.status?.toUpperCase() === 'DELIVERED').length,
        archived: nonRemovedLoads.filter(l => l.status?.toUpperCase() === 'ARCHIVED').length
      });
      
      setAllLoads(nonRemovedLoads);
      filterLoads(nonRemovedLoads);
    } catch (error) {
      console.error('Error loading loads:', error);
    } finally {
      setLoading(false);
    }
  }

  const filteredLoads = loads.filter(load => {
    // Status filter - convert to uppercase for comparison
    if (statusFilter !== 'all') {
      const loadStatus = load.status?.toUpperCase();
      if (statusFilter === 'dispatched') {
        // Handle dispatched filter to include both dispatched and in_transit
        if (loadStatus !== 'DISPATCHED' && loadStatus !== 'IN_TRANSIT') {
          return false;
        }
      } else if (statusFilter === 'booked' && loadStatus !== 'BOOKED') {
        return false;
      } else if (statusFilter === 'delivered' && loadStatus !== 'DELIVERED') {
        return false;
      } else if (statusFilter === 'archived' && loadStatus !== 'ARCHIVED') {
        return false;
      }
    }
    // Driver filter
    if (driverFilter !== 'all') {
      const driverName = `${load.drivers?.first_name || ''} ${load.drivers?.last_name || ''}`.trim();
      if (driverName !== driverFilter) return false;
    }
    // Truck filter
    if (truckFilter !== 'all' && (load.trucks?.unit_number?.toString() || '') !== truckFilter) return false;
    // Created By filter
    const createdByName = (load as any).created_by_profile?.full_name || 'System';
    if (createdByFilter !== 'all' && createdByName !== createdByFilter) return false;
    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      const driverName = `${load.drivers?.first_name || ''} ${load.drivers?.last_name || ''}`.trim().toLowerCase();
      const customerName = load.customers?.name?.toLowerCase() || '';
      const loadNumber = load.load_number?.toLowerCase() || '';
      const truckUnit = load.trucks?.unit_number?.toString().toLowerCase() || '';

      if (
        !loadNumber.includes(term) &&
        !customerName.includes(term) &&
        !driverName.includes(term) &&
        !truckUnit.includes(term)
      ) {
        return false;
      }
    }
    return true;
  });

  // Calculate pagination
  const totalPages = Math.ceil(filteredLoads.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedLoads = filteredLoads.slice(startIndex, endIndex);

  // Reset to page 1 when filters or search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, driverFilter, truckFilter, createdByFilter]);

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      booked: 'bg-slate-100 text-slate-700 border-slate-300',
      dispatched: 'bg-blue-100 text-blue-700 border-blue-300',
      en_route: 'bg-green-100 text-green-700 border-green-300',
      delivered: 'bg-emerald-100 text-emerald-700 border-emerald-300',
      invoiced: 'bg-purple-100 text-purple-700 border-purple-300',
      cancelled: 'bg-red-100 text-red-700 border-red-300',
    };
    return colors[status] || 'bg-slate-100 text-slate-700 border-slate-300';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // If load detail is selected, show detail view
  if (selectedLoadNumber) {
    return <LoadDetailView loadNumber={selectedLoadNumber} onBack={() => setSelectedLoadNumber(null)} />;
  }

  return (
    <div className="space-y-6">
      {/* Search Bar and Create Button */}
      <div className="flex items-center justify-between gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search by load #, customer, driver, or truck..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        <div className="flex items-center gap-3">
          {selectedLoads.size > 0 && (
            <button
              onClick={() => setShowBulkDeleteConfirm(true)}
              className="flex items-center gap-2 px-6 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-semibold shadow-sm"
            >
              <Trash2 className="w-5 h-5" />
              Delete Selected ({selectedLoads.size})
            </button>
          )}
          
          {canUseAIRateConf && (
            <button
              onClick={() => setShowAIUpload(true)}
              className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-lg hover:from-orange-600 hover:to-orange-700 transition-all font-semibold shadow-sm"
            >
              <FileText className="w-5 h-5" />
              AI Rate Confirmation
            </button>
          )}
          
          <button
            onClick={() => navigate('/loads/create')}
            className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold"
          >
            <Plus className="w-5 h-5" />
            Create Load
          </button>
        </div>
      </div>

      {/* Status Summary Cards and Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        {/* Status Summary Cards */}
        <button
          onClick={() => setStatusFilter('all')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-all ${
            statusFilter === 'all'
              ? 'border-blue-500 bg-blue-50'
              : 'border-slate-200 bg-white hover:border-blue-300'
          }`}
        >
          <span className="font-bold text-blue-600">{allLoads.length}</span>
          <span className="text-xs text-slate-600 ml-1">Total</span>
        </button>
        
        <button
          onClick={() => setStatusFilter('booked')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-all ${
            statusFilter === 'booked'
              ? 'border-blue-500 bg-blue-50'
              : 'border-slate-200 bg-white hover:border-blue-300'
          }`}
        >
          <span className="font-bold text-slate-600">
            {allLoads.filter(l => l.status?.toUpperCase() === 'BOOKED').length}
          </span>
          <span className="text-xs text-slate-600 ml-1">Booked</span>
        </button>
        
        <button
          onClick={() => setStatusFilter('dispatched')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-all ${
            statusFilter === 'dispatched'
              ? 'border-blue-500 bg-blue-50'
              : 'border-slate-200 bg-white hover:border-blue-300'
          }`}
        >
          <span className="font-bold text-blue-600">
            {allLoads.filter(l => l.status?.toUpperCase() === 'DISPATCHED' || l.status?.toUpperCase() === 'IN_TRANSIT').length}
          </span>
          <span className="text-xs text-slate-600 ml-1">Dispatched</span>
        </button>
        
        <button
          onClick={() => setStatusFilter('delivered')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-all ${
            statusFilter === 'delivered'
              ? 'border-blue-500 bg-blue-50'
              : 'border-slate-200 bg-white hover:border-blue-300'
          }`}
        >
          <span className="font-bold text-green-600">
            {allLoads.filter(l => l.status?.toUpperCase() === 'DELIVERED').length}
          </span>
          <span className="text-xs text-slate-600 ml-1">Delivered</span>
        </button>
        
        <button
          onClick={() => setStatusFilter('archived')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-all ${
            statusFilter === 'archived'
              ? 'border-purple-500 bg-purple-50'
              : 'border-slate-200 bg-white hover:border-purple-300'
          }`}
        >
          <span className="font-bold text-purple-600">
            {allLoads.filter(l => l.status?.toUpperCase() === 'ARCHIVED').length}
          </span>
          <span className="text-xs text-slate-600 ml-1">Archived</span>
        </button>
        
        <div className="h-8 w-px bg-slate-300 mx-2"></div>
        
        {/* Filter Dropdowns */}
        <select
          value={driverFilter}
          onChange={(e) => setDriverFilter(e.target.value)}
          className="px-3 py-2 text-sm rounded-lg border border-slate-200 bg-white hover:border-blue-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-200"
        >
          <option value="all">All Drivers</option>
          {[...new Set(
            allLoads
              .map(l => `${l.drivers?.first_name || ''} ${l.drivers?.last_name || ''}`.trim())
              .filter(Boolean)
          )].sort().map(driver => (
            <option key={driver} value={driver}>{driver}</option>
          ))}
        </select>

        <select
          value={truckFilter}
          onChange={(e) => setTruckFilter(e.target.value)}
          className="px-3 py-2 text-sm rounded-lg border border-slate-200 bg-white hover:border-blue-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-200"
        >
          <option value="all">All Trucks</option>
          {[...new Set(
            allLoads
              .map(l => l.trucks?.unit_number?.toString())
              .filter((u): u is string => Boolean(u))
          )].sort((a, b) => {
            const na = Number(a), nb = Number(b);
            if (!isNaN(na) && !isNaN(nb)) return na - nb;
            return a.localeCompare(b);
          }).map(truck => (
            <option key={truck} value={truck}>{truck}</option>
          ))}
        </select>
        
        <select
          value={createdByFilter}
          onChange={(e) => setCreatedByFilter(e.target.value)}
          className="px-3 py-2 text-sm rounded-lg border border-slate-200 bg-white hover:border-blue-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-200"
        >
          <option value="all">All Users</option>
          {[...new Set(loads.map(l => (l as any).created_by_profile?.full_name || 'System'))].map(user => (
            <option key={user} value={user}>{user}</option>
          ))}
        </select>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-3 py-2 text-center">
                  <input
                    type="checkbox"
                    checked={selectedLoads.size === filteredLoads.length && filteredLoads.length > 0}
                    onChange={toggleSelectAll}
                    className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
                  />
                </th>
                <th className="px-3 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-3 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase tracking-wider">
                  Created By
                </th>
                <th className="px-3 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase tracking-wider">
                  Load #
                </th>
                <th className="px-3 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-3 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase tracking-wider">
                  Pickup / Delivery Time
                </th>
                <th className="px-3 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase tracking-wider">
                  Driver / Truck
                </th>
                <th className="px-3 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase tracking-wider">
                  Pickup / Delivery Location
                </th>
                <th className="px-3 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase tracking-wider">
                  Broker
                </th>
                <th className="px-3 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase tracking-wider">
                  Miles
                </th>
                <th className="px-3 py-2 text-right text-[10px] font-semibold text-slate-600 uppercase tracking-wider">
                  Rate
                </th>
                <th className="px-3 py-2 text-right text-[10px] font-semibold text-slate-600 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {paginatedLoads.length === 0 ? (
                <tr>
                  <td colSpan={11} className="px-6 py-12 text-center text-slate-500">
                    No loads found
                  </td>
                </tr>
              ) : (
                paginatedLoads.map((load, index) => {
                  // Check if this is a new user group (different from previous load)
                  const currentUser = (load as any).created_by_profile?.full_name || 'System';
                  const previousUser = index > 0 
                    ? (paginatedLoads[index - 1] as any).created_by_profile?.full_name || 'System'
                    : null;
                  const isNewGroup = currentUser !== previousUser;
                  // Determine row color based on dispatch status
                  let rowColor = 'hover:bg-slate-50';
                  if (load.status === 'delivered') {
                    rowColor = 'bg-green-50 hover:bg-green-100';
                  } else if (load.status === 'dispatched' || load.status === 'in_transit') {
                    rowColor = 'bg-blue-50 hover:bg-blue-100';
                  }
                  
                  return (
                  <React.Fragment key={load.id}>
                    {/* Group Header Row */}
                    {isNewGroup && (
                      <tr className="bg-gradient-to-r from-slate-100 to-slate-50 border-t-2 border-slate-300">
                        <td colSpan={12} className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-1 h-6 bg-[#AC021A] rounded-full"></div>
                            <span className="font-bold text-sm text-slate-800">
                              {currentUser}
                            </span>
                            <span className="text-xs text-slate-500">
                              ({filteredLoads.filter(l => ((l as any).created_by_profile?.full_name || 'System') === currentUser).length} loads)
                            </span>
                          </div>
                        </td>
                      </tr>
                    )}
                    
                    {/* Load Row */}
                    <tr
                      key={load.id}
                      className={`${rowColor} transition-colors cursor-pointer`}
                    onClick={() => {
                      window.history.pushState({}, '', `/loads/${load.id}`);
                      window.dispatchEvent(new PopStateEvent('popstate'));
                    }}
                  >
                    <td className="px-3 py-2 text-center" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedLoads.has(load.id)}
                        onChange={() => toggleSelectLoad(load.id)}
                        className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <span className="text-xs text-slate-600">
                        {load.created_at ? new Date(load.created_at).toLocaleString() : 'N/A'}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <span className="text-xs text-slate-900 font-medium">
                        {(load as any).created_by_profile?.full_name || 'System'}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <button
                        onClick={() => {
                          window.history.pushState({}, '', `/loads/${load.id}`);
                          window.dispatchEvent(new PopStateEvent('popstate'));
                        }}
                        className="font-semibold text-[#AC021A] hover:text-[#8A0115] hover:underline transition-colors text-sm"
                      >
                        {load.load_number}
                      </button>
                      {(load as any).is_tonu && (
                        <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-800 border border-amber-300 align-middle">
                          TONU
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border ${getStatusColor(load.status)}`}>
                        {load.status.replace('_', ' ').toUpperCase()}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <div className="space-y-1">
                        <div className="flex items-center gap-1">
                          <span className="text-[10px] text-slate-500 font-medium">P:</span>
                          <span className="text-xs text-slate-900">
                            {load.pickup_date 
                              ? new Date(load.pickup_date.replace(' ', 'T')).toLocaleString('en-US', { 
                                  month: 'short', 
                                  day: 'numeric',
                                  hour: 'numeric',
                                  minute: '2-digit',
                                  hour12: true
                                })
                              : 'N/A'}
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="text-[10px] text-slate-500 font-medium">D:</span>
                          <span className={`text-xs ${load.rescheduled_delivery_date ? 'text-orange-600 font-semibold' : 'text-slate-900'}`}>
                            {load.rescheduled_delivery_date 
                              ? (() => {
                                  // Combine rescheduled date with time if available
                                  const dateStr = load.rescheduled_delivery_date;
                                  const timeStr = load.rescheduled_delivery_time_from || '00:00:00';
                                  const combinedDateTime = `${dateStr}T${timeStr}`;
                                  return new Date(combinedDateTime).toLocaleString('en-US', { 
                                    month: 'short', 
                                    day: 'numeric',
                                    hour: 'numeric',
                                    minute: '2-digit',
                                    hour12: true
                                  });
                                })()
                              : load.delivery_date 
                                ? new Date(load.delivery_date.replace(' ', 'T')).toLocaleString('en-US', { 
                                    month: 'short', 
                                    day: 'numeric',
                                    hour: 'numeric',
                                    minute: '2-digit',
                                    hour12: true
                                  })
                                : 'N/A'}
                          </span>
                          {load.rescheduled_delivery_date && (
                            <span className="text-[10px] text-orange-600" title="Rescheduled">⚠️</span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      {load.drivers && load.trucks ? (
                        <div className="space-y-1">
                          <div className="text-xs text-slate-900">
                            {load.drivers.first_name} {load.drivers.last_name}
                          </div>
                          <div className="flex items-center gap-1 text-[10px] text-slate-500">
                            <TruckIcon className="w-3 h-3" />
                            {load.trucks.unit_number}
                          </div>
                        </div>
                      ) : (
                        <span className="text-slate-400 text-xs">Unassigned</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <div className="space-y-1">
                        <div className="flex items-center gap-1">
                          <span className="text-[10px] text-slate-500 font-medium">P:</span>
                          <span className="text-xs text-slate-900">
                            {load.pickup_city && load.pickup_state 
                              ? `${load.pickup_city}, ${load.pickup_state}`
                              : load.pickup_date 
                                ? new Date(load.pickup_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                                : 'N/A'}
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="text-[10px] text-slate-500 font-medium">D:</span>
                          <span className="text-xs text-slate-900">
                            {load.delivery_city && load.delivery_state
                              ? `${load.delivery_city}, ${load.delivery_state}`
                              : load.delivery_date
                                ? new Date(load.delivery_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                                : 'N/A'}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <span className="text-slate-900 text-xs">{load.customers?.name || 'N/A'}</span>
                      <div className="text-[10px] text-slate-500">{load.commodity || ''}</div>
                      {load.weight && (
                        <div className="text-[10px] text-slate-500">{load.weight.toLocaleString()} lbs</div>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <span className="text-slate-900 text-xs">{load.total_miles || 0}</span>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <DollarSign className="w-3 h-3 text-slate-400" />
                        <span className="font-semibold text-slate-900 text-xs">
                          {Number(load.total_rate).toLocaleString()}
                        </span>
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-1.5">
                        {load.status.toLowerCase() === 'booked' && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setDispatchLoad(load);
                            }}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-md hover:from-blue-700 hover:to-blue-800 transition-all shadow-sm hover:shadow-md text-xs font-semibold"
                          >
                            <Send className="w-3.5 h-3.5" />
                            Dispatch
                          </button>
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/loads/edit/${load.id}`);
                          }}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border-2 border-slate-300 text-slate-700 rounded-md hover:border-slate-400 hover:bg-slate-50 transition-all shadow-sm hover:shadow text-xs font-medium"
                        >
                          <Edit className="w-3.5 h-3.5" />
                          Edit
                        </button>
                        {(load.status !== 'removed' && load.status !== 'delivered')
                          || (load.status === 'delivered' && profile?.role === 'Superadmin')
                          || (load.status === 'delivered' && (load as any).is_tonu) ? (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setLoadToDelete(load);
                              setShowDeleteConfirm(true);
                            }}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border-2 border-red-300 text-red-600 rounded-md hover:border-red-400 hover:bg-red-50 transition-all shadow-sm hover:shadow text-xs font-medium"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                            Remove
                          </button>
                        ) : null}
                        {load.status === 'removed' && profile?.role === 'Superadmin' && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setLoadToDelete(load);
                              setShowDeleteConfirm(true);
                            }}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-600 text-white rounded-md hover:bg-red-700 transition-all shadow-sm hover:shadow text-xs font-semibold"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                            Delete Permanently
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                  </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="mt-4 flex items-center justify-between px-4">
          <div className="text-sm text-slate-600">
            Showing {startIndex + 1} to {Math.min(endIndex, filteredLoads.length)} of {filteredLoads.length} results
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1.5 text-sm border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Previous
            </button>
            <div className="flex items-center gap-1">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => {
                if (
                  page === 1 ||
                  page === totalPages ||
                  (page >= currentPage - 1 && page <= currentPage + 1)
                ) {
                  return (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                        currentPage === page
                          ? 'bg-blue-600 text-white'
                          : 'border border-slate-300 hover:bg-slate-50'
                      }`}
                    >
                      {page}
                    </button>
                  );
                } else if (page === currentPage - 2 || page === currentPage + 2) {
                  return <span key={page} className="px-2 text-slate-400">...</span>;
                }
                return null;
              })}
            </div>
            <button
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-1.5 text-sm border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {dispatchLoad && (
        <DispatchModal
          load={dispatchLoad}
          onClose={() => setDispatchLoad(null)}
          onSuccess={() => {
            setDispatchLoad(null);
            loadLoads();
          }}
        />
      )}

      {showDeleteConfirm && loadToDelete && (
        <ConfirmModal
          isOpen={showDeleteConfirm}
          title={loadToDelete.status === 'removed' ? 'Delete Load Permanently' : 'Remove Load'}
          message={
            loadToDelete.status === 'removed'
              ? `Are you sure you want to PERMANENTLY DELETE load ${loadToDelete.load_number}? This action cannot be undone and will delete all associated documents.`
              : `Are you sure you want to remove load ${loadToDelete.load_number}? The load number will be renamed and marked as removed.`
          }
          confirmText={loadToDelete.status === 'removed' ? 'Delete Permanently' : 'Remove'}
          type="danger"
          onConfirm={handleDeleteLoad}
          onCancel={() => {
            setShowDeleteConfirm(false);
            setLoadToDelete(null);
          }}
        />
      )}

      {showBulkDeleteConfirm && (
        <ConfirmModal
          isOpen={showBulkDeleteConfirm}
          title="Delete Multiple Loads"
          message={`Are you sure you want to delete ${selectedLoads.size} selected load(s)? Active loads will be marked as removed, and removed loads will be permanently deleted (Superadmin only).`}
          confirmText={`Delete ${selectedLoads.size} Load(s)`}
          type="danger"
          onConfirm={handleBulkDelete}
          onCancel={() => setShowBulkDeleteConfirm(false)}
        />
      )}

      {/* Always render AIRateConfUpload to keep queue state */}
      <div className={showAIUpload ? '' : 'hidden'}>
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-slate-900">AI Rate Confirmation Upload</h2>
              <button
                onClick={() => setShowAIUpload(false)}
                className="text-slate-400 hover:text-slate-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6">
              <AIRateConfUpload 
                onSuccess={() => {
                  setShowAIUpload(false);
                  loadLoads();
                }}
                isModalOpen={showAIUpload}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
